import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';

import { CareerIcon } from 'atoms';
import { ICareerAction } from 'core';
import { careerColor } from 'services/careerTokens';
import { careerLayout, careerType } from 'services/careerType';

import { actionText } from '../model/careerPlanText';

interface ICareerNextSteps {
  actions: ICareerAction[];
}

/**
 * «Możesz też» — не больше двух дополнительных действий (ТЗ 0071, п. 4.7;
 * «визуальное упрощение», 7).
 *
 * Бывшее «W tym tygodniu», затем «Najbliższe kroki»: это СЛЕДУЮЩИЕ пункты того
 * же списка правил, который выбрал основное действие, — повторить его или
 * разойтись с ним им негде. Теперь это тихий список строк без карточек и цветных
 * квадратов: вторая жёлтая кнопка или вторая карточка спорили бы с основным
 * действием.
 */
export const CareerNextSteps = memo(({ actions }: ICareerNextSteps) => {
  const { t } = useTranslation();
  if (!actions.length) return null;

  return (
    <section aria-labelledby="career-next-steps-title">
      <Heading id="career-next-steps-title">{t('careerCenter.plan.nextStepsTitle')}</Heading>
      <List>
        {actions.slice(0, 2).map((action) => {
          const text = actionText(t, action);
          return (
            <li key={action.id}>
              <Row to={action.route}>
                <Icon aria-hidden>
                  <CareerIcon name={text.icon} size={20} />
                </Icon>
                <Body>
                  <Title>{text.title}</Title>
                  <Reason>{text.reason}</Reason>
                </Body>
                <Arrow aria-hidden>
                  <CareerIcon name="arrowRight" size={16} />
                </Arrow>
              </Row>
            </li>
          );
        })}
      </List>
    </section>
  );
});

const Heading = styled.h2`
  margin: 0 0 16px;
  color: ${careerColor.text};
  ${careerType.h2()};
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;

  & > li + li {
    border-top: 1px solid ${careerColor.line};
  }
`;

const Row = styled(Link)`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-height: ${careerLayout.primaryButton}px;
  padding: 12px 8px;
  border-radius: ${careerLayout.controlRadius}px;
  color: ${careerColor.text};
  text-decoration: none;

  &:hover {
    color: ${careerColor.text};
    background: ${careerColor.hover};
  }
`;

const Icon = styled.span`
  flex: none;
  display: inline-flex;
  margin-top: 2px;
  color: ${careerColor.text2};
`;

const Body = styled.span`
  flex: 1 1 auto;
  min-width: 0;
`;

const Title = styled.span`
  display: block;
  overflow-wrap: anywhere;
  ${careerType.bodyStrong()};
`;

const Reason = styled.span`
  display: block;
  margin-top: 2px;
  color: ${careerColor.text2};
  ${careerType.meta()};
`;

const Arrow = styled.span`
  flex: none;
  display: inline-flex;
  align-self: center;
  color: ${careerColor.text2};
`;
